import { TranslationKey } from './../../../../types/translation/TranslationKey';
import React from 'react';
import dayjs from 'dayjs';
import { Order } from '../../../../types/order/order';
import { ProductMasterData } from '../../../../types/product_master_data/productMasterData';
import { ProductTransaction, ProductTransactionParentType } from '../../../../types/productTransaction';
import { TagRelation } from '../../../../types/tag/tagRelation';
import { t } from '../../../../types/translation/Translator';
import { User } from '../../../../types/user';
import { BaseFilter } from '../common/BaseFilter';
import { DateTimeFilter } from '../DateTimeFilter';
import { FilterStorageKey, FilterProps } from '../common/filter.util';
import { ProductMasterDataFilter } from '../ProductMasterDataFilter';
import { StockLocationFilter } from '../StockLocationFilter';
import { TagFilter } from '../TagFilter';
import { UserFilter } from '../UserFilter';
import { Container } from '../../../../types/container';
import { CustomFieldFilter } from '../CustomFieldFilter';
import { CustomField, CustomFieldEntityType } from '../../../../types/custom_field/customField';
import { itemsContainText } from '../../../../util/string.util';
import { StockLocation } from '../../../../types/stock_location';
import { OrderFilter } from '../OrderFilter';
import { EntityType } from '../../../../types/comment';
import { Company } from '../../../../types/company';
import { CompanyPartnerFilter } from '../CompanyPartnerFilter/CompanyPartnerFilter';
import { StockLocationFilterMode } from '../StockLocationFilter/StockLocationFilter';

export class ProductTransactionFilter extends BaseFilter<ProductTransaction> {
  users: Map<string, User>;
  productMasterData: Map<string, ProductMasterData>;
  orders: Map<string, Order>;
  tagRelations: Map<string, TagRelation>;
  containers: Map<string, Container>;
  customFields: Map<string, CustomField>;
  stockLocations: Map<string, StockLocation>;
  productTransactions: Map<string, ProductTransaction>;
  company: Company;

  constructor(
    users: Map<string, User>,
    productMasterData: Map<string, ProductMasterData>,
    orders: Map<string, Order>,
    tagRelations: Map<string, TagRelation>,
    containers: Map<string, Container>,
    customFields: Map<string, CustomField>,
    stockLocations: Map<string, StockLocation>,
    productTransactions: Map<string, ProductTransaction>,
    company: Company,
    key?: string,
  ) {
    super(FilterStorageKey.PRODUCT_TRANSACTION, key);
    this.users = users;
    this.productMasterData = productMasterData;
    this.orders = orders;
    this.tagRelations = tagRelations;
    this.containers = containers;
    this.customFields = customFields;
    this.stockLocations = stockLocations;
    this.productTransactions = productTransactions;
    this.company = company;
  }

  toLabel(): string {
    return t(TranslationKey.productMasterData).singular.label;
  }

  toCount(): number {
    return 0;
  }

  toIcon(): JSX.Element {
    return <></>;
  }

  toDropdownContent(): JSX.Element {
    return <></>;
  }

  toInnerContent(): string | JSX.Element {
    return <></>;
  }

  filterOptions = () => [
    ...(this.company.partner ? [new CompanyPartnerFilter()] : []),
    new ProductMasterDataFilter(this.customFields, this.tagRelations, this.company),
    new DateTimeFilter(),
    new StockLocationFilter(this.tagRelations, this.company, StockLocationFilterMode.source),
    new StockLocationFilter(this.tagRelations, this.company, StockLocationFilterMode.destination),
    new UserFilter(this.users),
    new TagFilter(EntityType.stockLocation),
    new TagFilter(EntityType.pmd),
    ...CustomFieldFilter.for(CustomFieldEntityType.productTransaction, this.productTransactions, this.customFields),
    ...CustomFieldFilter.for(CustomFieldEntityType.productMasterData, this.productMasterData, this.customFields),
    ...CustomFieldFilter.for(CustomFieldEntityType.order, this.orders, this.customFields),
  ];

  search = (item: ProductTransaction, filter: string) => {
    const productMasterData = this.productMasterData.get(item.product?.pmdId || '');
    const user = this.users.get(item.processedBy || '');
    const order = this.orders.get(item.parentId || '');

    const container = this.containers.get(item.product.containerId || '')?.identifier;
    const toContainer =
      this.containers.get(item.product.toContainerId || '')?.identifier || item.metadata?.toContainer?.identifier;
    const fromContainer =
      this.containers.get(item.product.fromContainerId || '')?.identifier || item.metadata?.fromContainer?.identifier;

    return itemsContainText(filter, [
      productMasterData?.productName,
      productMasterData?.productNumber,
      user?.firstName,
      user?.lastName,
      user?.email,
      order?.number,
      item.product?.serialNbr,
      item.product?.lpn,
      item.product?.countryOfOrigin,
      container,
      toContainer,
      fromContainer,
    ]);
  };

  filteredItems(items: ProductTransaction[], query: string, filterProps: FilterProps) {
    return items
      .filter(item => {
        const productMasterData = this.productMasterData.get(item.product?.pmdId || '');
        const order = this.orders.get(item.parentId || '');

        const stockLocationTag =
          TagFilter.filterBy(
            filterProps,
            EntityType.stockLocation,
            this.tagRelations,
            item.product.fromStockLocationId,
          ) ||
          TagFilter.filterBy(filterProps, EntityType.stockLocation, this.tagRelations, item.product.toStockLocationId);

        const pmdTag = TagFilter.filterBy(filterProps, EntityType.pmd, this.tagRelations, item.product.pmdId);

        const stockLocation = [
          StockLocationFilter.filter(
            filterProps,
            item.product.fromStockLocationId ? this.stockLocations.get(item.product.fromStockLocationId) : undefined,
            StockLocationFilterMode.source,
          ),
          StockLocationFilter.filter(
            filterProps,
            item.product.toStockLocationId ? this.stockLocations.get(item.product.toStockLocationId) : undefined,
            StockLocationFilterMode.destination,
          ),
        ].every(value => value === null || value);

        return [
          stockLocationTag,
          pmdTag,
          stockLocation,
          DateTimeFilter.filter(filterProps, new Date(item.processedAt || item.createdAt)),
          ProductMasterDataFilter.filter(filterProps, productMasterData, this.customFields, this.tagRelations),
          OrderFilter.filter(filterProps, order, this.customFields),
          ProductTransactionFilter.filter(filterProps, item, this.customFields),
          this.search(item, query),
        ].every(value => value === null || value);
      })
      .sort((a, b) =>
        dayjs(new Date(a.processedAt || a.createdAt)).isBefore(new Date(b.processedAt || b.createdAt)) ? 1 : -1,
      );
  }

  static filter(
    filterProps: FilterProps,
    item?: ProductTransaction,
    customFields: Map<string, CustomField> = new Map(),
  ) {
    if (item && item.parentType === ProductTransactionParentType.transaction) return false;
    if (item && filterProps.user?.size && !filterProps.user.has(item.createdBy)) return false;
    if (item && filterProps.company?.size && !filterProps.company.has(item.companyId)) return false;
    return CustomFieldFilter.filter(filterProps, item, customFields, CustomFieldEntityType.productTransaction);
  }
}
